import { Directive, ElementRef, HostListener, inject } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

/** Subpixel slack — a 0.3 px difference is rounding, not a cut-off word. */
const OVERFLOW_TOLERANCE_PX = 1;

/**
 * Shows the host's tooltip only when its text is actually being ellipsised.
 *
 * Anything text-shaped in a width-capped box can truncate — a kørsel row's four lines in a narrow
 * pane, a table cell holding a 200-character skabelonnavn — but a tooltip that repeats a fully
 * visible string is noise, and there is no CSS way to ask "did this ellipsise?".
 *
 * Pair it with `triggers="manual"`: NgbTooltip's own mouseenter handler would race this one, and
 * flipping `disableTooltip` from a competing listener is only correct if ours happens to run first.
 * Owning the trigger removes the ordering question entirely.
 *
 * Measured per hover rather than watched: `scrollWidth` forces layout, and doing that once when a
 * pointer stops on a row is free, where a ResizeObserver per line across a virtual list is not.
 *
 * The content comes from the element's own text, so `ngbTooltip` is left empty in the template.
 * Binding it would mean writing the same expression twice per line, and the two could drift — the
 * tooltip exists to show what did not fit, so it has to be exactly what is on screen.
 */
@Directive({ selector: '[appTruncateTooltip]' })
export class TruncateTooltipDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tooltip = inject(NgbTooltip, { self: true });

  @HostListener('mouseenter')
  protected onEnter(): void {
    const element = this.host.nativeElement;
    if (element.scrollWidth - element.clientWidth <= OVERFLOW_TOLERANCE_PX) return;

    // Template indentation lands in textContent as newlines and runs of spaces.
    const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return;

    this.tooltip.ngbTooltip = text;
    // Set here rather than in every template that uses the directive — the wider box is part of
    // what this directive is for, not a per-call-site choice. Styled in styles.scss, since the
    // tooltip is rendered on <body> and no component stylesheet can reach it.
    this.tooltip.tooltipClass = 'truncate-tip';
    this.tooltip.open();
  }

  @HostListener('mouseleave')
  protected onLeave(): void {
    this.tooltip.close();
  }
}
